<template>
  <v-container>
     <v-row class="mb-4 align-center">
      <v-col cols="7" sm="4" md="2" class="d-flex align-center">
        <SelectYear v-model="selectedYear" class="flex-grow-1" />
      </v-col>
      <v-col cols="5" sm="auto" class="d-flex align-center justify-end justify-sm-start">
        <v-menu>
          <template #activator="{ props }">
            <v-btn
              v-bind="props"
              color="primary"
              variant="tonal"
              :loading="exporting"
              :disabled="!ranking.races.length && !ranking.categories.length"
              prepend-icon="mdi-download"
            >
              {{ $t('ranking.export') }}
            </v-btn>
          </template>
          <v-list density="compact">
            <v-list-item @click="downloadExport('pdf')">
              <v-list-item-title>{{ $t('ranking.export_pdf') }}</v-list-item-title>
            </v-list-item>
            <v-list-item @click="downloadExport('xlsx')">
              <v-list-item-title>{{ $t('ranking.export_xlsx') }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-col>
      <v-spacer class="d-none d-sm-flex" />
      <v-col cols="12" sm="auto" class="d-none d-sm-flex align-center justify-sm-end">
        <h3 class="text-body-1 mb-0">{{ $t('ranking.drop_count', { count: ranking.drop_count }) }}</h3>
      </v-col>
    </v-row>

    <RankingTable 
    :loading="loading" 
    :ranking="ranking" 
    @refreshRanking="() => fetchRanking(selectedYear)"
    />

    <!-- Zobrazení zprávy, pokud nejsou závody -->
    <v-alert v-if="!ranking.races.length && !ranking.categories.length && !loading" type="info" class="mt-4">
      Zatím nejsou vypsané žádné závody do národního žebříčku.
    </v-alert>

  </v-container>
</template>

<script setup>
import { ref, watch } from 'vue';
import apiClient from '/api.js';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import RankingTable from '@/components/shared/RankingTable.vue';
import SelectYear from '@/components/shared/SelectYear.vue';
import { useSnackbarStore } from '@/stores/snackbarStore';

const loading = ref(true);
const exporting = ref(false);
const ranking = ref({ races: [], categories: [], drop_count: 0 });

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const snackbar = useSnackbarStore();
const thisYear = new Date().getFullYear();

const parseYear = (v) => {
  const y = Number(v);
  return Number.isFinite(y) ? y : thisYear;
};

const selectedYear = ref(parseYear(route.params.year));

const fetchRanking = async (year) => {
  loading.value = true;

  try {
    const response = await apiClient.get(`/ranking/national/${year}/`);
    ranking.value = response.data || { races: [], categories: [], drop_count: 0 };
  } catch (error) {
    const status = error?.response?.status;
    if (status === 404) {
      ranking.value = { races: [], categories: [], drop_count: 0 };}
    console.error(error);
  } finally {
    loading.value = false;
  }
};

const downloadExport = async (type) => {
  exporting.value = true;
  try {
    const response = await apiClient.get(
      `/ranking/national/${selectedYear.value}/export/?type=${type}`,
      { responseType: 'blob' }
    );
    const disposition = response.headers['content-disposition'] || '';
    const match = disposition.match(/filename="?([^"]+)"?/i);
    const filename = match?.[1] || `narodni_zebricek_ROB_${selectedYear.value}.${type}`;
    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error(error);
    snackbar.error(t('ranking.export_failed'));
  } finally {
    exporting.value = false;
  }
};

watch(
  () => route.params.year,
  (ny) => {
    const y = parseYear(ny);
    if (y !== selectedYear.value) selectedYear.value = y;
    fetchRanking(y);
  },
  { immediate: true }
);

// Změna v SelectYear => změň URL (spustí watch výše)
watch(selectedYear, (y) => {
  if (parseYear(route.params.year) !== y) {
    router.replace({ name: 'RankingNational', params: { year: String(y) } });
  }
});

</script>
